"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  findInformalLanguage,
  type InformalKind,
  type InformalMatch,
} from "@/lib/tool-kit/writing-analysis/informal-language";

/** English strings. A translated content file overrides these through `ui`. */
export const INFORMAL_LANGUAGE_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, an essay or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Nowadays, a lot of students get stressed before exams, and there are tons of reasons why. In our survey, kids who worked part-time were way more likely to report anxiety. We wanted to find out whether sleep played a part, so we looked into the data from 120 participants. Plus, the results were pretty clear: the students who slept less got lower grades. Is that a coincidence? Schools need to come up with better ways to deal with this stuff!",
  enterPrompt:
    "Paste or type some text to find conversational words and phrases, with formal alternatives.",
  results: "Results",
  words: "Words",
  informal: "Informal words and phrases",
  phrasal: "Phrasal verbs",
  per1000: "Per 1,000 words",
  exclamations: "Exclamation marks",
  questions: "Questions",
  quotedSkipped:
    "one:{count} match inside quotation marks was left alone — quoted speech is evidence, not your register.|other:{count} matches inside quotation marks were left alone — quoted speech is evidence, not your register.",
  scope:
    "Contractions, filler words, first-person pronouns, -ly adverbs, clichés and wordy phrases have their own checkers and are not counted here.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with informal language highlighted",
  legendWord: "Informal word",
  legendPhrasal: "Phrasal verb",
  legendPunctuation: "Exclamation mark or question",
  hoverHint: "Hover over a highlight to see formal alternatives.",
  titleAlternatives: "Try: {alternatives}",
  titleExclamation:
    "An exclamation mark — academic prose lets the finding speak",
  titleQuestion:
    "A question — fine for a research question; a rhetorical one usually reads better as a statement",
  noneFound:
    "No informal words or phrasal verbs from the list found. Read it aloud once more for tone — no list catches everything.",
  tableHeading: "Words and phrases to reconsider",
  colPhrase: "Word or phrase",
  colTimes: "Times",
  colType: "Type",
  colTry: "Try instead",
  kinds: {
    vocabulary: "Informal word",
    phrasal: "Phrasal verb",
    vague: "Vague word",
    spoken: "Spoken form",
    intensifier: "Informal intensifier",
    exclamation: "Exclamation mark",
    question: "Question",
  },
  notes: {
    anyway: "Usually removable; to concede a point, try “in any case”.",
    get: "Depends on the meaning: obtain, receive, become, understand — or rephrase.",
    kindOf:
      "Flagged only as a hedge (“kind of difficult”), never as “a kind of”.",
    like: "Flagged only as a filler between commas; for examples, use “such as”.",
    lookAt: "Common in many fields; “examine” or “consider” is more formal.",
    plus: "As a conjunction joining ideas; “plus or minus” is fine.",
    pretty: "Flagged as an adverb (“pretty clear”), not after “a”.",
    vague: "Name the thing: what exactly is the “stuff” or the “thing”?",
  },
  punctuationHeading: "Exclamation marks and questions",
  exclamationNote:
    "one:{count} exclamation mark. Academic writing states findings without them; let the evidence carry the emphasis.|other:{count} exclamation marks. Academic writing states findings without them; let the evidence carry the emphasis.",
  questionNote:
    "one:{count} question. A research question is fine; a rhetorical question usually reads better as a statement of what you will show.|other:{count} questions. A research question is fine; a rhetorical question usually reads better as a statement of what you will show.",
} as const;

type Strings = typeof INFORMAL_LANGUAGE_CHECKER_STRINGS;

const TONES: Record<InformalKind, HighlightTone> = {
  vocabulary: "amber",
  vague: "amber",
  spoken: "amber",
  intensifier: "amber",
  phrasal: "sky",
  exclamation: "rose",
  question: "rose",
};

function titleFor(match: InformalMatch, strings: Strings): string {
  if (match.kind === "exclamation") {
    return strings.titleExclamation;
  }
  if (match.kind === "question") {
    return strings.titleQuestion;
  }
  return t(strings.titleAlternatives, {
    alternatives: match.alternatives.join(", "),
  });
}

export default function InformalLanguageCheckerWidget() {
  const strings = useToolUi(INFORMAL_LANGUAGE_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(() => findInformalLanguage(deferred), [deferred]);

  const highlights: TextMatch[] = report.matches.map((match) => ({
    start: match.start,
    end: match.end,
    category: match.kind,
    title: titleFor(match, strings),
  }));
  const hasText = deferred.trim().length > 0;
  const punctuation = report.exclamations + report.questions;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="fw-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.informal}
                value={fmt(report.informal)}
              />
              <StatCard label={strings.phrasal} value={fmt(report.phrasal)} />
              <StatCard
                label={strings.per1000}
                value={fmt(report.per1000, { maximumFractionDigits: 1 })}
              />
              <StatCard
                label={strings.exclamations}
                value={fmt(report.exclamations)}
              />
              <StatCard
                label={strings.questions}
                value={fmt(report.questions)}
              />
              <StatCard label={strings.words} value={fmt(report.words)} />
            </StatGrid>
          </div>
          <p className={HINT}>{strings.scope}</p>
          {report.quotedSkipped > 0 ? (
            <p className={HINT}>
              {tPlural(strings.quotedSkipped, locale, report.quotedSkipped, {
                count: fmt(report.quotedSkipped),
              })}
            </p>
          ) : null}

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <p className="flex flex-wrap gap-3 text-sm">
              <span className={HIGHLIGHT_TONES.amber}>
                {strings.legendWord}
              </span>
              <span className={HIGHLIGHT_TONES.sky}>
                {strings.legendPhrasal}
              </span>
              <span className={HIGHLIGHT_TONES.rose}>
                {strings.legendPunctuation}
              </span>
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {report.rows.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className={TABLE}>
                  <thead>
                    <tr>
                      <th className={TH} scope="col">
                        {strings.colPhrase}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colTimes}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colType}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colTry}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row) => (
                      <tr key={row.key}>
                        <td className={`${TD} font-medium`}>{row.key}</td>
                        <td className={TD}>{fmt(row.count)}</td>
                        <td className={`${TD} text-muted-foreground`}>
                          {strings.kinds[row.kind]}
                        </td>
                        <td className={TD}>
                          {row.alternatives.join(", ")}
                          {row.note ? (
                            <span className="mt-1 block text-muted-foreground text-xs">
                              {strings.notes[row.note]}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {punctuation > 0 ? (
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.punctuationHeading}</h3>
              {report.exclamations > 0 ? (
                <Notice>
                  {tPlural(
                    strings.exclamationNote,
                    locale,
                    report.exclamations,
                    { count: fmt(report.exclamations) }
                  )}
                </Notice>
              ) : null}
              {report.questions > 0 ? (
                <Notice>
                  {tPlural(strings.questionNote, locale, report.questions, {
                    count: fmt(report.questions),
                  })}
                </Notice>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
